// CI Registry — the core primitive. Every other page is a filtered view.

import { pbAuthedFetch } from '../authHeaders';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ConfidenceBar } from '../components/provenance/ConfidenceBar';
import { StalenessIndicator } from '../components/provenance/StalenessIndicator';
import { SourceChip } from '../components/provenance/SourceChip';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';

interface CI {
  id: string;
  ci_type: string;
  canonical_key: string;
  display_name: string;
  confidence: number;
  lifecycle_state: string;
  last_seen_at: string;
}

interface CIDetail extends CI {
  assertions: any[];
  relationships: any[];
  findings: any[];
}

export function RegistryPage() {
  const [selectedCI, setSelectedCI] = useState<string | null>(null);
  const [drawerTab, setDrawerTab] = useState<'assertions' | 'relationships' | 'findings'>('assertions');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cis'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ci?limit=1000');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: detail } = useQuery({
    queryKey: ['ci', selectedCI],
    queryFn: async () => {
      const res = await pbAuthedFetch(`/api/ci/${selectedCI}`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json() as Promise<CIDetail>;
    },
    enabled: !!selectedCI,
  });

  const cis = data?.cis || [];
  const total = data?.total || 0;

  const ciColumns: Column<CI>[] = [
    {
      key: 'canonical_key',
      label: 'Identifier',
      sortable: true,
      accessor: (row) => row.canonical_key,
      render: (row) => <span className="td--id">{row.canonical_key}</span>,
    },
    {
      key: 'ci_type',
      label: 'Type',
      sortable: true,
      accessor: (row) => row.ci_type,
      render: (row) => (
        <span className="pill"><i></i> {row.ci_type}</span>
      ),
      filterOptions: [
        { value: 'endpoint', label: 'Endpoint' },
        { value: 'server', label: 'Server' },
        { value: 'service', label: 'Service' },
        { value: 'account', label: 'Account' },
        { value: 'group', label: 'Group' },
        { value: 'gpo', label: 'GPO' },
        { value: 'network_device', label: 'Network Device' },
        { value: 'software_entitlement', label: 'Software' },
      ],
    },
    {
      key: 'display_name',
      label: 'Display Name',
      sortable: true,
      accessor: (row) => row.display_name,
    },
    {
      key: 'lifecycle_state',
      label: 'Lifecycle',
      sortable: true,
      accessor: (row) => row.lifecycle_state,
      render: (row) => {
        const pillClass =
          row.lifecycle_state === 'active' ? 'pill--ok' :
          row.lifecycle_state === 'ghost' || row.lifecycle_state === 'orphan' ? 'pill--risk' :
          row.lifecycle_state === 'stale' ? 'pill--warn' : '';
        return (
          <span className={`pill ${pillClass}`}>
            <i></i> {row.lifecycle_state}
          </span>
        );
      },
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'stale', label: 'Stale' },
        { value: 'ghost', label: 'Ghost' },
        { value: 'orphan', label: 'Orphan' },
        { value: 'retired', label: 'Retired' },
      ],
    },
    {
      key: 'confidence',
      label: 'Confidence',
      sortable: true,
      accessor: (row) => row.confidence,
      render: (row) => <ConfidenceBar value={row.confidence} />,
    },
    {
      key: 'last_seen',
      label: 'Last Seen',
      sortable: true,
      accessor: (row) => row.last_seen_at,
      render: (row) => <StalenessIndicator lastSeenAt={row.last_seen_at} />,
      dateField: true,
    },
  ];

  const ciTable = useTableControls(cis, ciColumns, {
    storageKey: 'registry',
    keyAccessor: (row: CI) => row.id,
  });

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Registry</p>
              <h1 className="pagehead__title">CI Registry</h1>
              <div className="pagehead__facts">
                <span className="fact">Configuration Item Inventory</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading configuration items…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Registry</p>
              <h1 className="pagehead__title">CI Registry</h1>
              <div className="pagehead__facts">
                <span className="fact">Configuration Item Inventory</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load registry</h3>
                <p className="state__body">
                  Could not connect to the CI API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Registry</p>
            <h1 className="pagehead__title">CI Registry</h1>
            <div className="pagehead__facts">
              <span className="fact">Total: <b>{total}</b> configuration items</span>
              {total > 0 && <span className="fact">Showing: <b>{cis.length}</b></span>}
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* CI Table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Inventory</p>
              <h2 className="box__title">Configuration Items</h2>
            </div>
          </div>
          <div className="box__body box__body--flush">
            <DataTable
              columns={ciColumns}
              visibleColumns={ciTable.visibleColumns}
              data={ciTable.paged}
              search={ciTable.search}
              onSearchChange={ciTable.setSearch}
              sortKey={ciTable.sortKey}
              sortDir={ciTable.sortDir}
              onSort={ciTable.setSort}
              page={ciTable.page}
              totalPages={ciTable.totalPages}
              totalFiltered={ciTable.totalFiltered}
              pageSize={ciTable.pageSize}
              onPageChange={ciTable.setPage}
              onPageSizeChange={ciTable.setPageSize}
              activeFilters={ciTable.activeFilters}
              onFilter={ciTable.setFilter}
              onClearFilters={ciTable.clearFilters}
              searchPlaceholder="Search CIs..."
              keyAccessor={(row) => row.id}
              onRowClick={(row) => setSelectedCI(row.id)}
              emptyMessage="No CIs found. Ingest data from sources to populate the registry."
              timeRange={ciTable.timeRange}
              onTimeRangeChange={ciTable.setTimeRange}
              hiddenColumns={ciTable.hiddenColumns}
              onToggleColumn={ciTable.toggleColumn}
              selectedIds={ciTable.selectedIds}
              onToggleSelect={ciTable.toggleSelect}
              onSelectAll={ciTable.selectAll}
              onClearSelection={ciTable.clearSelection}
              savedViews={ciTable.savedViews}
              onSaveView={ciTable.saveView}
              onLoadView={ciTable.loadView}
              onDeleteView={ciTable.deleteView}
              onExportCSV={ciTable.exportCSV}
              onExportExcel={ciTable.exportExcel}
              showSelection={true}
            />
          </div>
          <div className="box__foot">
            <span className="fact">{total} total CIs in registry</span>
          </div>
        </div>
      </div>

      {/* CI Detail Drawer */}
      {selectedCI && detail && (
        <>
          <div className="scrim is-on" onClick={() => setSelectedCI(null)} />
          <div className="drawer is-on" role="dialog" aria-label="CI detail">
            <div className="drawer__head">
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 600 }}>
                  {detail.display_name || detail.canonical_key}
                </h2>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-xs)', color: 'rgba(255,255,255,.6)', marginTop: 2 }}>
                  {detail.canonical_key}
                </div>
              </div>
              <button className="ibtn" onClick={() => setSelectedCI(null)} aria-label="Close drawer">✕</button>
            </div>

            <div className="drawer__body">
              {/* CI metadata strip */}
              <div style={{ display: 'flex', gap: 'var(--sp-4)', marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>Type</div>
                  <span className="pill"><i></i> {detail.ci_type}</span>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>State</div>
                  <span className={`pill ${detail.lifecycle_state === 'active' ? 'pill--ok' : detail.lifecycle_state === 'ghost' || detail.lifecycle_state === 'orphan' ? 'pill--risk' : 'pill--warn'}`}>
                    <i></i> {detail.lifecycle_state}
                  </span>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-45)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 2 }}>Confidence</div>
                  <ConfidenceBar value={detail.confidence} />
                </div>
              </div>

              {/* Tabs */}
              <div className="tabs" role="tablist">
                <button
                  className="tab"
                  role="tab"
                  aria-selected={drawerTab === 'assertions'}
                  onClick={() => setDrawerTab('assertions')}
                >
                  Assertions <span className="tab__count">{detail.assertions?.length || 0}</span>
                </button>
                <button
                  className="tab"
                  role="tab"
                  aria-selected={drawerTab === 'relationships'}
                  onClick={() => setDrawerTab('relationships')}
                >
                  Relationships <span className="tab__count">{detail.relationships?.length || 0}</span>
                </button>
                <button
                  className="tab"
                  role="tab"
                  aria-selected={drawerTab === 'findings'}
                  onClick={() => setDrawerTab('findings')}
                >
                  Findings <span className="tab__count">{detail.findings?.length || 0}</span>
                </button>
              </div>

              {/* Assertions tab */}
              {drawerTab === 'assertions' && (
                detail.assertions && detail.assertions.length > 0 ? (
                  <div className="tbl-scroll">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th><button className="th">Attribute</button></th>
                          <th><button className="th">Value</button></th>
                          <th><button className="th">Source</button></th>
                          <th><button className="th th--static">Observed</button></th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.assertions.map((a: any) => (
                          <tr key={a.id}>
                            <td className="td--id">{a.attribute}</td>
                            <td className="td--id">
                              {typeof a.value === 'object' ? JSON.stringify(a.value) : String(a.value ?? '—')}
                            </td>
                            <td><SourceChip source={a.source_key} observedAt={a.observed_at} /></td>
                            <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                              {new Date(a.observed_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="state">
                    <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <h3 className="state__title">No assertions</h3>
                    <p className="state__body">No assertions have been recorded for this CI.</p>
                  </div>
                )
              )}

              {/* Relationships tab */}
              {drawerTab === 'relationships' && (
                detail.relationships && detail.relationships.length > 0 ? (
                  <div className="tbl-scroll">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th><button className="th">From</button></th>
                          <th><button className="th">Relationship</button></th>
                          <th><button className="th">To</button></th>
                          <th><button className="th">Source</button></th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.relationships.map((r: any) => (
                          <tr key={r.id}>
                            <td className="td--id">{r.from_name || r.from_ci_id}</td>
                            <td><span className="pill"><i></i> {r.rel_type}</span></td>
                            <td className="td--id">{r.to_name || r.to_ci_id}</td>
                            <td><SourceChip source={r.source_key} observedAt={r.observed_at} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="state">
                    <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <h3 className="state__title">No relationships</h3>
                    <p className="state__body">No relationships have been recorded for this CI.</p>
                  </div>
                )
              )}

              {/* Findings tab */}
              {drawerTab === 'findings' && (
                detail.findings && detail.findings.length > 0 ? (
                  <div className="tbl-scroll">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th><button className="th">Severity</button></th>
                          <th><button className="th">Type</button></th>
                          <th><button className="th">Title</button></th>
                          <th><button className="th">State</button></th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.findings.map((f: any) => (
                          <tr key={f.id}>
                            <td>
                              <span className={`pill pill--${f.severity === 'critical' || f.severity === 'high' ? 'risk' : f.severity === 'medium' ? 'warn' : 'ok'}`}>
                                <i></i> {f.severity}
                              </span>
                            </td>
                            <td className="td--id">{f.finding_type}</td>
                            <td className="td--name">{f.title}</td>
                            <td><span className="pill"><i></i> {f.state}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="state">
                    <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <h3 className="state__title">No findings</h3>
                    <p className="state__body">No findings have been recorded for this CI.</p>
                  </div>
                )
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
